import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { colors } from '../styles/tokens'
import { EmptyState, ErrorBanner, SuccessBanner, SkeletonRows } from '../components/Feedback'
import EstimationBreadcrumb from '../components/EstimationBreadcrumb'
import { useAuth } from '../../context/AuthContext'

// Platform-admin "Customer Tenders" console.
//
// SECURITY MODEL: every operation is a database function that re-checks
// is_platform_admin() on the server (estimation.admin_*). The check on
// profile.role below is a UI hint only -- it never replaces the server
// check. This page shows administrative METADATA only (names, organization,
// counts). It does not read, and the database does not expose through
// these functions, any tender content (items, rates, revisions' data).

const DISP_STYLE = {
  keep: { bg: '#F0FDF4', text: '#15803D', label: 'KEEP' },
  remove: { bg: '#FEF2F2', text: '#B91C1C', label: 'REMOVE (snapshot saved)' },
  history: { bg: '#F3F4F6', text: '#4B5563', label: 'HISTORY (untouched)' },
  block: { bg: '#FEF3C7', text: '#92400E', label: 'BLOCKS MOVE' },
}

function Badge({ d }) {
  const s = DISP_STYLE[d] || DISP_STYLE.block
  return (
    <span style={{ background: s.bg, color: s.text, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  )
}

const inputStyle = { padding: '7px 10px', border: '1px solid #DDD9D0', borderRadius: 5, fontSize: 13, minWidth: 180 }
const labelStyle = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: colors.textSecondary, display: 'block', marginBottom: 4 }
const btn = (bg, disabled) => ({
  fontSize: 13, fontWeight: 700, padding: '8px 16px', background: disabled ? '#9CA3AF' : bg, color: '#fff',
  border: 'none', borderRadius: 6, cursor: disabled ? 'not-allowed' : 'pointer',
})

export default function AdminCustomerTendersPage() {
  const { profile } = useAuth()
  const isAdminHint = profile?.role === 'admin'

  const [tenders, setTenders] = useState(null)
  const [orgs, setOrgs] = useState([])
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  const [form, setForm] = useState({ organization_id: '', name: '', client_name: '', location: '' })
  const [creating, setCreating] = useState(false)

  const [moveFor, setMoveFor] = useState(null) // tender row being moved
  const [destOrg, setDestOrg] = useState('')
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [ack, setAck] = useState(false)
  const [moveError, setMoveError] = useState(null)
  const [lastMove, setLastMove] = useState(null) // { move_id, tender_name }

  const load = useCallback(async () => {
    setError(null)
    const [t, o] = await Promise.all([
      supabase.rpc('admin_list_customer_tenders'),
      supabase.schema('public').from('organizations').select('id, name').order('name'),
    ])
    if (t.error) { setError(t.error.message); setTenders([]); return }
    if (o.error) { setError(o.error.message); return }
    setTenders(t.data || [])
    setOrgs(o.data || [])
  }, [])

  useEffect(() => { if (isAdminHint) load() }, [isAdminHint, load])

  async function handleCreate(e) {
    e.preventDefault()
    setNotice(null); setError(null)
    if (!form.organization_id) { setError('Select the customer organization.'); return }
    if (!form.name.trim()) { setError('Tender name is required.'); return }
    setCreating(true)
    const { error: err } = await supabase.rpc('admin_create_tender', {
      p_organization_id: form.organization_id,
      p_name: form.name.trim(),
      p_client_name: form.client_name.trim() || null,
      p_location: form.location.trim() || null,
    })
    setCreating(false)
    if (err) { setError(err.message); return }
    const orgName = orgs.find((o) => o.id === form.organization_id)?.name || ''
    setNotice(`Tender "${form.name.trim()}" created under ${orgName}. You have no role on it by design — add the customer engineer with Users → Quick Add User (same e-mail and company, then pick this tender).`)
    setForm({ organization_id: form.organization_id, name: '', client_name: '', location: '' })
    load()
  }

  function openMove(t) {
    setMoveFor(t); setDestOrg(''); setPreview(null); setAck(false); setMoveError(null)
  }
  function closeMove() { setMoveFor(null); setPreview(null); setMoveError(null) }

  async function runPreview() {
    setMoveError(null); setPreview(null); setAck(false)
    if (!destOrg) { setMoveError('Select the destination organization.'); return }
    setBusy(true)
    const { data, error: err } = await supabase.rpc('admin_preview_tender_move', {
      p_tender_id: moveFor.tender_id, p_dest_org_id: destOrg,
    })
    setBusy(false)
    if (err) { setMoveError(err.message); return }
    setPreview(data)
  }

  async function runMove() {
    if (!preview?.can_execute || !ack) return
    setBusy(true); setMoveError(null)
    const { data, error: err } = await supabase.rpc('admin_move_tender_to_org', {
      p_tender_id: moveFor.tender_id, p_dest_org_id: destOrg,
      p_preview_hash: preview.preview_hash, p_confirm: true,
    })
    setBusy(false)
    if (err) { setMoveError(err.message); return }
    setLastMove({ move_id: data.move_id, tender_name: moveFor.tender_name })
    setNotice(`Tender "${moveFor.tender_name}" moved to ${preview.dest_org_name}. Removed records were saved and can be restored (move id ${data.move_id}).`)
    closeMove()
    load()
  }

  async function runRestore() {
    if (!lastMove) return
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Restore the move of "${lastMove.tender_name}"? The tender returns to its previous organization and all removed records are re-created exactly as they were.`)) return
    setBusy(true); setError(null)
    const { error: err } = await supabase.rpc('admin_restore_tender_move', { p_move_id: lastMove.move_id, p_confirm: true })
    setBusy(false)
    if (err) { setError(err.message); return }
    setNotice(`Move of "${lastMove.tender_name}" restored.`)
    setLastMove(null)
    load()
  }

  if (!isAdminHint) {
    return (
      <div>
        <EstimationBreadcrumb />
        <EmptyState title="Platform administrators only" description="This page is restricted. All operations are also enforced in the database." />
      </div>
    )
  }

  const destOptions = orgs.filter((o) => o.id !== moveFor?.organization_id)

  return (
    <div>
      <EstimationBreadcrumb />
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>Customer Tenders</h1>
        <p style={{ fontSize: 13, color: colors.textSecondary, margin: '4px 0 0' }}>
          Platform administration only. Shows administrative details, never tender contents.{' '}
          <Link to="/estimation" style={{ color: colors.primary }}>Back to Tenders</Link>
        </p>
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={notice} />
      {lastMove && (
        <div style={{ marginBottom: 16 }}>
          <button disabled={busy} onClick={runRestore} style={btn('#B45309', busy)}>
            Undo last move ({lastMove.tender_name})
          </button>
        </div>
      )}

      <form onSubmit={handleCreate} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end', marginBottom: 20, padding: 16, background: '#F5F1EA', borderRadius: 8 }}>
        <div>
          <label style={labelStyle}>Customer organization *</label>
          <select value={form.organization_id} onChange={(e) => setForm((f) => ({ ...f, organization_id: e.target.value }))} style={inputStyle}>
            <option value="">Select…</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Tender name *</label>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Client</label>
          <input value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Location</label>
          <input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} style={inputStyle} />
        </div>
        <button type="submit" disabled={creating} style={btn('#1A7A4A', creating)}>{creating ? 'Creating…' : 'Create for customer'}</button>
      </form>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'auto' }}>
        {tenders === null && !error && <div style={{ padding: '8px 20px' }}><SkeletonRows rows={4} cols={6} /></div>}
        {tenders?.length === 0 && <div style={{ padding: 24 }}><EmptyState title="No tenders" description="Nothing to show." /></div>}
        {tenders?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}`, textAlign: 'left' }}>
                {['Organization', 'Tender', 'Client / Location', 'Revisions', 'Roles / Perms / Pending invites', 'Mapped project', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', color: colors.textSecondary }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenders.map((t) => (
                <tr key={t.tender_id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '10px 14px' }}>
                    {t.organization_name || <span style={{ color: colors.danger, fontWeight: 600 }}>— none (cannot be moved)</span>}
                  </td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{t.tender_name}</td>
                  <td style={{ padding: '10px 14px' }}>{t.client_name || '—'}<div style={{ color: colors.textMuted, fontSize: 12 }}>{t.location || '—'}</div></td>
                  <td style={{ padding: '10px 14px' }}>{t.revision_count}</td>
                  <td style={{ padding: '10px 14px' }}>{t.role_count} / {t.permission_count} / {t.pending_invitation_count}</td>
                  <td style={{ padding: '10px 14px' }}>{t.mapped_project_name || '—'}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    <button onClick={() => openMove(t)} style={btn(colors.primary, false)}>Move to organization</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {moveFor && (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', padding: 24, zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 24, width: 'min(860px, 100%)' }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Move tender to another organization</h2>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: colors.textSecondary }}>
              <b>{moveFor.tender_name}</b> — current organization: <b>{moveFor.organization_name || 'none'}</b>
            </p>

            <ErrorBanner message={moveError} />

            <div style={{ display: 'flex', gap: 10, alignItems: 'end', marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Destination organization</label>
                <select value={destOrg} onChange={(e) => { setDestOrg(e.target.value); setPreview(null); setAck(false) }} style={inputStyle}>
                  <option value="">Select…</option>
                  {destOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <button onClick={runPreview} disabled={busy || !destOrg} style={btn(colors.primary, busy || !destOrg)}>
                {busy && !preview ? 'Checking…' : 'Preview move'}
              </button>
            </div>

            {preview && (
              <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 14, marginBottom: 14, fontSize: 13 }}>
                <div style={{ marginBottom: 8 }}>
                  <b>{preview.source_org_name || 'NONE'}</b> → <b>{preview.dest_org_name}</b>
                </div>

                {preview.blockers?.length > 0 && (
                  <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 6, padding: 10, marginBottom: 10 }}>
                    <b>This move is blocked. Nothing will be changed.</b>
                    <ul style={{ margin: '6px 0 0 18px' }}>{preview.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
                  </div>
                )}

                <RecordTable title="Estimation roles on this tender" rows={preview.est_user_role} cols={[['email', 'User'], ['role_code', 'Role']]} />
                <RecordTable title="Explicit permissions" rows={preview.user_permissions} cols={[['email', 'User'], ['permission', 'Permission']]} />
                <RecordTable title="Invitations" rows={preview.invitation_access} cols={[['email', 'E-mail'], ['role_code', 'Role'], ['applied', 'Applied']]} />
                <RecordTable title="Project mapping" rows={preview.mapping ? [preview.mapping] : []} cols={[['project_name', 'Project']]} />

                {preview.can_execute && (
                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10 }}>
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} style={{ marginTop: 3 }} />
                    <span>I reviewed the exact records above. Records marked REMOVE will be deleted after a full snapshot is saved, and can be restored with “Undo last move”.</span>
                  </label>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={closeMove} disabled={busy} style={btn('#6B7280', busy)}>Cancel</button>
              <button onClick={runMove} disabled={busy || !preview?.can_execute || !ack} style={btn('#B91C1C', busy || !preview?.can_execute || !ack)}>
                {busy && preview ? 'Moving…' : 'Confirm move'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function RecordTable({ title, rows, cols }) {
  if (!rows || rows.length === 0) {
    return <div style={{ margin: '6px 0', color: colors.textMuted }}>{title}: none</div>
  }
  return (
    <div style={{ margin: '10px 0' }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{title} ({rows.length})</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderTop: `1px solid ${colors.border}` }}>
              {cols.map(([k]) => <td key={k} style={{ padding: '4px 8px' }}>{String(r[k] ?? '—')}</td>)}
              <td style={{ padding: '4px 8px' }}><Badge d={r.disposition} /></td>
              <td style={{ padding: '4px 8px', color: colors.textSecondary, fontSize: 12 }}>{r.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
